/**
 * Adobe Spectrum Theme (Dark Mode)
 * Enterprise design system theme inspired by Adobe Spectrum 2
 * Dark backgrounds with vibrant semantic colors for visibility
 * Primary: Light Blue (#64B5F6), Secondary: Light Red (#EF5350), Accent: Green (#81C784)
 */

import { adobeDarkColors } from '../tokens/colors.js';
import { spacing, zIndex } from '../tokens/spacing.js';
import { typography } from '../tokens/typography.js';
import { borderRadius, shadows, transitions } from '../tokens/effects.js';

export const adobeDarkTheme = {
  name: 'adobe-dark',
  label: 'Adobe Spectrum Dark',
  description: 'Dark mode theme inspired by Adobe Spectrum 2 design system',
  isDark: true,

  // Color palette
  colors: {
    primary: adobeDarkColors.primary,
    primaryLight: adobeDarkColors.primaryLight,
    primaryDark: adobeDarkColors.primaryDark,
    primaryHover: adobeDarkColors.primaryDark,
    primaryOpacity: adobeDarkColors.primaryOpacity20,

    secondary: adobeDarkColors.secondary,
    secondaryLight: adobeDarkColors.secondaryLight,
    secondaryDark: adobeDarkColors.secondaryDark,
    secondaryOpacity: adobeDarkColors.secondaryOpacity20,

    accent: adobeDarkColors.accent,
    accentLight: adobeDarkColors.accentLight,
    accentDark: adobeDarkColors.accentDark,
    accentOpacity: adobeDarkColors.accentOpacity20,

    // Semantic colors
    error: adobeDarkColors.error,
    errorLight: adobeDarkColors.errorLight,
    success: adobeDarkColors.success,
    successLight: adobeDarkColors.successLight,
    warning: adobeDarkColors.warning,
    warningLight: adobeDarkColors.warningLight,
    info: adobeDarkColors.info,
    infoLight: adobeDarkColors.infoLight,

    // Neutrals
    background: adobeDarkColors.backgroundLight,
    backgroundAlt: adobeDarkColors.backgroundDark,
    backgroundDark: adobeDarkColors.backgroundDark,
    backgroundNeutral: '#2A2A2A',
    surface: adobeDarkColors.surfaceLight,
    surfaceElevated: adobeDarkColors.surfaceElevated,

    // Text
    textPrimary: adobeDarkColors.white,
    textSecondary: adobeDarkColors.lightGray400,
    textTertiary: adobeDarkColors.textTertiary,
    textInverse: adobeDarkColors.white,

    // Borders
    borderDefault: adobeDarkColors.darkGray800,
    borderSubtle: adobeDarkColors.darkGray900,
    borderInput: adobeDarkColors.borderInput,
  },

  // Spacing
  spacing,

  // Z-index
  zIndex,

  // Typography
  typography,

  // Border radius
  borderRadius,

  // Shadows
  shadows,

  // Transitions
  transitions,

  // Component-specific tokens
  components: {
    button: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      transition: transitions.default,
      fontSize: typography.fontSize.body,
      fontWeight: typography.fontWeight.semibold,
      // SYN-2738 (A9): fundos/texto do DSButton por tema (--button-*).
      primaryBg: adobeDarkColors.primaryButton,
      primaryBgHover: adobeDarkColors.primaryDark,
      // C12 (2026-10-05): texto do botão ghost/de texto. Aqui = a cor primária de sempre.
      ghostText: adobeDarkColors.primary,
      secondaryBg: adobeDarkColors.secondary,
      secondaryBgHover: adobeDarkColors.secondaryDark,
      accentText: adobeDarkColors.backgroundDark,
    },
    card: {
      padding: spacing.xl,
      borderRadius: borderRadius.lg,
      backgroundColor: adobeDarkColors.surfaceLight,
      boxShadow: shadows.base,
    },
    input: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      borderColor: adobeDarkColors.darkGray800,
      focusBorderColor: adobeDarkColors.primary,
      fontSize: typography.fontSize.body,
    },
    chip: {
      paddingX: spacing.md,
      paddingY: spacing.xs,
      borderRadius: borderRadius.full,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.medium,
    },
    badge: {
      paddingX: spacing.sm,
      paddingY: spacing.xs,
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.bold,
    },
    sidebar: {
      backgroundColor: adobeDarkColors.backgroundLight,
      iconSelected: adobeDarkColors.primary,
      iconDefault: adobeDarkColors.lightGray400,
      iconDisabled: adobeDarkColors.mediumGray500,
      iconStatic: adobeDarkColors.darkGray800,
      divider: adobeDarkColors.darkGray800,
    },
  },

  // Vuetify theme configuration
  vuetifyTheme: {
    dark: true,
    colors: {
      primary: adobeDarkColors.primary,
      secondary: adobeDarkColors.secondary,
      accent: adobeDarkColors.accent,
      error: adobeDarkColors.error,
      warning: adobeDarkColors.warning,
      info: adobeDarkColors.info,
      success: adobeDarkColors.success,
      background: adobeDarkColors.backgroundLight,
      surface: adobeDarkColors.surfaceLight,
    },
  },
};
